import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import Input, { Select } from '../Input';
import client from '../../utils/client';
import { getCountry, getIdAndHashForWebSale } from '../../utils/helpers';

const country = getCountry();

const WebSaleSettings = ({ settings, superAdmin, golfclubName, onClose }) => {
  const [inProgress, setInProgress] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const {
    handleSubmit,
    register,
    // reset,
    watch,
    reset,
    formState: { errors }
  } = useForm({ defaultValues: settings });

  useEffect(() => {
    const settingsNotEmpty = Object.keys(settings).length > 0;
    if (settingsNotEmpty) {
      reset(settings);
    }
  }, [settings]);

  const createSupplier = async () => {
    setInProgress('create-supplier');
    setError(null);
    setSuccess(null);

    const { error: supplierError } = await client(
      'admin-gc/web-sales/fortnox/supplier',
      {},
      'post'
    );

    setInProgress(null);

    if (supplierError) {
      setError(supplierError.message);
    } else {
      //   showSettings();
    }
  };

  const saveSettings = async (formData) => {
    // console.log('formData', formData);

    setInProgress('saving-settings');
    setError(null);
    setSuccess(null);

    const { error: settingsError } = await client('admin-gc/web-sales/settings', {
      body: formData
    });

    if (settingsError) {
      setError(settingsError?.message || 'Något gick fel');
    } else {
      setSuccess('Informationen är sparad');
    }
    setInProgress(null);
  };

  const previewData = () => {
    const data = getIdAndHashForWebSale();
    window.open(
      `https://www.golfhaftet.com/admin-gc/golfclub/preview.php?clubId=${data.id}&clubHash=${data.hash}`,
      '__blank'
    );
  };

  const offersPayment = parseInt(watch('offersPayment'), 10);
  const sellsPhysicalCards = parseInt(watch('sellsPhysicalCards'), 10);
  const delivery = parseInt(watch('delivery'), 10);

  const fortnoxPaymentInfoMissingSE =
    country === 'SE' && (!settings?.bgNr || !settings?.orgNr || !settings?.vatNr);
  const fortnoxPaymentInfoMissingDE =
    country === 'DE' &&
    (!settings?.swiftCode || !settings?.ibanNr || !settings.accountHolder || !settings?.vatNr);

  const golfamorePaymentAvailable =
    ['SE', 'DE'].includes(country) &&
    ((country === 'SE' && !fortnoxPaymentInfoMissingSE) ||
      (country === 'DE' && !fortnoxPaymentInfoMissingDE)) &&
    settings.fortnoxSupplierNumber;

  const { t } = useTranslation();

  return (
    <Modal isOpen centered>
      <form onSubmit={handleSubmit(saveSettings)}>
        <ModalHeader>{t('web-sales.settings-title', 'Ändra inställningar')}</ModalHeader>
        <ModalBody>
          {/* {inProgress === 'fetching-settings' && <Spinner />} */}
          {superAdmin && (
            <div className="mb-4">
              <h4>Super admin information</h4>
              <div>
                {country === 'SE' && (
                  <p className="small mb-0">Organization number: {settings.orgNr}</p>
                )}
                <p className="small mb-0">VAT number: {settings.vatNr}</p>
                {country === 'SE' && <p className="small mb-0">Bankgiro (SE): {settings.bgNr}</p>}
                {country === 'DE' && <p className="small mb-0">Swift: {settings.swiftCode}</p>}
                {country === 'DE' && <p className="small mb-0">IBAN: {settings.ibanNr}</p>}
                {country === 'DE' && (
                  <p className="small mb-0">Account holder: {settings.accountHolder}</p>
                )}
                <p className="small mb-0">
                  Fortnox supplier number: {settings.fortnoxSupplierNumber}
                </p>
              </div>

              {fortnoxPaymentInfoMissingSE && (
                <Alert color="info" className="p-2">
                  <p className="small p-0 m-0">
                    Any of organization-, VAT- or bankgiro-number missing. Update in admin before
                    you can activate payments via Golfamore
                  </p>
                </Alert>
              )}
              {fortnoxPaymentInfoMissingDE && (
                <Alert color="info" className="p-2">
                  <p className="small p-0 m-0">
                    Any of account holder, swift code, iban- or VAT-number missing. Update in admin
                    before you can activate payments via Golfamore
                  </p>
                </Alert>
              )}

              {!fortnoxPaymentInfoMissingSE &&
                !fortnoxPaymentInfoMissingDE &&
                !settings.fortnoxSupplierNumber && (
                  <Alert color="info" className="p-2">
                    <p className="small p-0 m-0">
                      Fortnox supplier number is missing. Create a supplier in Fortnox with the
                      button.
                    </p>
                    <Button
                      size="sm"
                      className="mt-2"
                      onClick={createSupplier}
                      disabled={inProgress === 'create-supplier'}
                    >
                      {inProgress === 'create-supplier' && <Spinner size="sm" />} Create a supplier
                      for {golfclubName} in Fortnox
                    </Button>
                  </Alert>
                )}

              {error && <Alert color="danger">{error}</Alert>}
            </div>
          )}
          <FormGroup>
            <Label>{t('web-sales.offer-payment', 'Erbjuder ni online-betalning?')}</Label>
            <Select
              name="offersPayment"
              register={register}
              rules={{ required: true }}
              error={errors.offersPayment}
              disabled={inProgress}
            >
              <option value="2" disabled={!superAdmin || !golfamorePaymentAvailable}>
                {t('web-sales.yes-ga-payment-handle', 'Ja, via Golfamore')}
              </option>
              <option value="1">
                {t('web-sales.yes-club-payment-handle', 'Ja, vi hanterar det själv')}
              </option>
              <option value="0">{t('default.no', 'Nej')}</option>
            </Select>

            {offersPayment === 2 && (
              <p className="alert alert-info small ">
                {t(
                  'web-sales.settings-payment-ga-text',
                  // eslint-disable-next-line max-len
                  'Tänk på att inte ange någon information om betalning under Välkomst- och Tack-text nedan. Det hanteras av Golfamore.'
                )}
              </p>
            )}
          </FormGroup>
          {offersPayment === 1 && (
            <FormGroup>
              <Label className="mb-1">{t('web-sales.settings-payment-text', 'Betal-text')}</Label>
              <span className="small d-flex mb-1">
                {t(
                  'web-sales.settings-payment-info',
                  'Ange hur kunden betalar till er, tex via Swish eller bankgiro.'
                )}
              </span>
              <Input
                type="textarea"
                name="paymentText"
                register={register}
                rules={{ required: true }}
                error={errors.paymentText}
                // value={settings.paymentText || ''}
                // onChange={onSettingsChange}
                disabled={inProgress}
                style={{ height: '100px' }}
              />
            </FormGroup>
          )}
          <FormGroup>
            <Label>{t('web-sales.sells-physical-cards', 'Säljer ni fysiska kort')}</Label>
            <Select
              name="sellsPhysicalCards"
              register={register}
              rules={{ required: true }}
              error={errors.sellsPhysicalCards}
              // value={settings.sellsPhysicalCards}
              // onChange={onSettingsChange}
              disabled={inProgress}
            >
              <option value="1">{t('default.yes', 'Ja')}</option>
              <option value="0">{t('default.no', 'Nej')}</option>
            </Select>
          </FormGroup>
          {sellsPhysicalCards === 1 && (
            <FormGroup>
              <Label className="mb-1">
                {t('web-sales.settings-delivery', 'Erbjuder ni leverans av beställningar')}?
              </Label>
              <span className="small d-flex mb-1">
                {t('web-sales.settings-delivery-info', 'Gäller endast fysiska kort')}
              </span>
              <Select
                name="delivery"
                register={register}
                rules={{ required: true }}
                error={errors.delivery}
                // value={settings.delivery}
                // onChange={onSettingsChange}
                disabled={inProgress}
              >
                <option value="">
                  {t('web-sales.settings-delivery-options', 'Välj leveranssätt')}
                </option>
                <option value="1">
                  {t('web-sales.yes-club-handle', 'Ja, vi på klubben hanterar leveransen')}
                </option>
                {/* <option value="2" disabled={settings.offersPayment !== 2}>
                      {t('web-sales.yes-ga-handle', 'Ja, Golfamore hanterar leveransen')}
                    </option> */}
                <option value="0">
                  {t('web-sales.no-only-club', 'Nej, beställningar hämtas på klubben')}
                </option>
              </Select>
            </FormGroup>
          )}
          {offersPayment === 2 && delivery === 1 && (
            <FormGroup>
              <Label className="mb-1">
                {t(
                  'web-sales.settings-delivery-fee',
                  'Ange fraktavgift för leverans av fysiska kort'
                )}
              </Label>
              <Input
                name="deliveryFee"
                register={register}
                rules={{ required: true }}
                error={errors.deliveryFee}
                disabled={inProgress}
              />
            </FormGroup>
          )}
          <FormGroup>
            <Label className="mb-1">{t('web-sales.settings-welcome-text', 'Välkomst-text')}</Label>
            <span className="small d-flex mb-1">
              {t(
                'web-sales.settings-campaign-info',
                'Kampanjinformation behöver inte anges, det hanteras automatiskt.'
              )}
            </span>
            <Input
              type="textarea"
              name="welcomeText"
              register={register}
              // rules={{ required: true }}
              // error={errors.welcomeText}
              // value={settings.welcomeText || ''}
              // onChange={onSettingsChange}
              disabled={inProgress}
              style={{ height: '100px' }}
            />
          </FormGroup>
          <FormGroup>
            <Label className="mb-1">{t('web-sales.settings-thanks-text', 'Tack-text')}</Label>
            <span className="small d-flex mb-1">
              {t(
                'web-sales.settings-campaign-info',
                'Kampanjinformation behöver inte anges, det hanteras automatiskt.'
              )}
            </span>
            <Input
              type="textarea"
              name="thanksText"
              register={register}
              // rules={{ required: true }}
              // error={errors.thanksText}
              // value={settings.thanksText || ''}
              // onChange={onSettingsChange}
              disabled={inProgress}
              style={{ height: '100px' }}
            />
          </FormGroup>
          <FormGroup>
            <Label>
              {t('web-sales.settings-order-email', 'E-post för beställningar')}{' '}
              <span className="small">
                ({t('web-sales.settings-order-email-help', 'Om tom används klubbens e-post adress')}
                )
              </span>
            </Label>
            <Input
              name="orderEmail"
              register={register}
              rules={{
                required: false,
                pattern: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/
              }}
              error={errors.orderEmail}
              // value={settings.orderEmail || ''}
              // onChange={onSettingsChange}
              disabled={inProgress}
            />
          </FormGroup>
          <FormGroup>
            <Label>
              {t('web-sales.settings-identified-url', 'Identifierad url för er beställnings-sida')}
              <span className="small d-flex mb-0">
                {t(
                  'web-sales.settings-reload-website',
                  'Ladda om er beställningssida för att identifiera er url'
                )}
              </span>
            </Label>
            <Input
              name="clubUrl"
              register={register}
              // rules={{ required: true }}
              // error={errors.clubUrl}
              // value={settings.clubUrl || ''}
              // onChange={onSettingsChange}
              disabled={inProgress}
            />
          </FormGroup>

          <Alert color="info">
            {t(
              'web-sales.settings-note-saved-changes',
              'Observera att sparade ändringar direkt syns på er hemsida om ni implementerat vårt skript'
            )}
          </Alert>

          {success && <Alert color="success">{success}</Alert>}
          {error && <Alert color="danger">{error}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={onClose}>
            {t('default.close')}
          </Button>
          <Button type="submit" color="success">
            {t('default.save')}
          </Button>
          <Button color="info" onClick={previewData}>
            {t('default.preview', 'Förhandsgranska')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default WebSaleSettings;
